import type { ReactElement, ReactNode } from 'react';
import { cloneElement, useId, useMemo, useState } from 'react';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export function Tooltip({ content, children, position = 'right' }: TooltipProps) {
  const tooltipId = useId();
  const [isOpen, setIsOpen] = useState(false);

  const mergedChild = useMemo(() => {
    const childProps = children.props as Record<string, unknown>;
    const onMouseEnter = childProps.onMouseEnter as ((e: unknown) => void) | undefined;
    const onMouseLeave = childProps.onMouseLeave as ((e: unknown) => void) | undefined;
    const onFocus = childProps.onFocus as ((e: unknown) => void) | undefined;
    const onBlur = childProps.onBlur as ((e: unknown) => void) | undefined;

    return cloneElement(children as ReactElement<any>, {
      'aria-describedby': (isOpen ? tooltipId : undefined) as string | undefined,
      onMouseEnter: (e: unknown) => {
        onMouseEnter?.(e);
        setIsOpen(true);
      },
      onMouseLeave: (e: unknown) => {
        onMouseLeave?.(e);
        setIsOpen(false);
      },
      onFocus: (e: unknown) => {
        onFocus?.(e);
        setIsOpen(true);
      },
      onBlur: (e: unknown) => {
        onBlur?.(e);
        setIsOpen(false);
      },
    });
  }, [children, isOpen, tooltipId]);

  return (
    <span className={styles.wrapper}>
      {mergedChild}
      {isOpen && (
        <span id={tooltipId} role="tooltip" className={`${styles.tooltip} ${styles[position]}`}>
          {content}
        </span>
      )}
    </span>
  );
}

Tooltip.displayName = 'Tooltip';

